import React, {useEffect, useState} from 'react';
import VehicleEditorTemplate from './VehicleEditorTemplate';
import vehicleService from '../../services/VehicleService';
import {useNavigate} from 'react-router-dom';
import {useTitle} from '../dashboard/TitleContext';
import authService from '../../services/AuthService';
import {saveAs} from 'file-saver';
import axios from 'axios';
import {API_URL} from '../../App';
import {useSnackbar} from '../../util/SnackbarProvider';
import cloudStorageFileService from "../../services/CloudStorageFileService";

const LOCAL_STORAGE_ITEM_NAME = "vehicle-invoices-rows-per-page"

const invoiceTypeMap = {
    "INVOICE": "Számla",
    "ADVANCE_INVOICE": "Előlegszámla",
    "FEE_REQUEST": "Díjbekérő",
    "RECEIPT": "Nyugta"
};

const paymentTypeMap = {
    "CASH": "Készpénz",
    "TRANSFER": "Átutalás",
    "CREDIT_CARD": "Bankkártya",
    "CASH_ON_DELIVERY": "Utánvét"
};

const VehicleEditor = () => {

    const urlParts = window.location.pathname.split('/')
    const id = urlParts[3] === 'create' ? undefined : urlParts[3]

    useTitle([
        {name: 'Jármű Lista', url: '/dashboard/vehicles'},
        {name: 'Jármű', url: '/dashboard/vehicles/' + (id || 'create')}
    ])

    const [tableData, setTableData] = useState({
        page: 0,
        rowsPerPage: localStorage.getItem(LOCAL_STORAGE_ITEM_NAME) || 25,
        totalCount: 0
    })
    const {sendAlertMessage} = useSnackbar()
    const [vehicle, setVehicle] = useState({});
    const [message, setMessage] = useState('');
    const [files, setFiles] = useState([]);
    const [waitingForServer, setWaitingForServer] = useState(false);
    const navigate = useNavigate()

    useEffect(() => {
        if (!id) return
        retrieveVehicle()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const retrieveVehicle = () => {
        vehicleService.findById(id)
            .then(response => setVehicle(response))
            .catch(error => console.error(error));
    }

    useEffect(() => {
        if (!id) return
        vehicleService.getInvoices(id, {size: tableData.rowsPerPage, page: tableData.page})
            .then(response => {
                const result = response.content.map((invoice) => {
                    const invoiceType = invoiceTypeMap[invoice.invoiceType] || "";
                    const paymentType = paymentTypeMap[invoice.paymentType] || "";
                    return {...invoice, invoiceType: invoiceType, paymentType: paymentType}
                })
                setTableData((prev) => ({...prev, totalCount: response.totalElements, rows: result}))
            })
            .catch(error => console.error(error));
    }, [id, tableData.rowsPerPage, tableData.page]);

    const onChange = (event) => {
        const {name, value} = event.target;
        setVehicle((prevVehicle) => ({
            ...prevVehicle,
            [name]: value,
        }));
    };

    const create = (e) => {
        e.preventDefault();
        vehicleService.createVehicle(vehicle)
            .then(() => navigate(-1))
            .catch(error => setMessage(error.message))
    };

    const update = async (e) => {
        e.preventDefault();
        setWaitingForServer(true)
        const formData = new FormData();
        formData.append('request', new Blob([JSON.stringify(vehicle)], {type: "application/json"}));
        files.forEach((file) => {
            formData.append('files', file);
        });

        const headers = {'Authorization': 'Bearer ' + authService.getCurrentUser().accessToken};
        try {
            const response = await axios.patch(API_URL + `/vehicles/${id}`, formData, {headers: headers});
            if (response) {
                sendAlertMessage('Jármű sikeresen mentve')
                setFiles([])
                retrieveVehicle()
            }
        } catch (error) {
            sendAlertMessage('Jármű mentése sikertelen', 'error')
        }
        setWaitingForServer(false)
    };

    const downloadSimpleInvoice = async (id, fileName) => {
        const headers = {'Authorization': 'Bearer ' + authService.getCurrentUser().accessToken};
        axios({
            url: API_URL + "/simple-invoices/" + id + "/document",
            params: {fileName: fileName},
            method: 'GET',
            responseType: 'blob',
            headers: headers,
        })
            .then(response => {
                const contentType = response.headers['content-type'];
                const blob = new Blob([response.data], {type: contentType});
                const url = window.URL.createObjectURL(blob);
                console.log(contentType)

                if (contentType === 'application/pdf' || contentType.startsWith('image/')) {
                    window.open(url)
                } else {
                    saveAs(blob, fileName);
                    sendAlertMessage('Fájl sikeresen letöltve')
                }
            })
            .catch(error => {
                console.error(error)
                sendAlertMessage('Fájl letöltése sikertelen', 'error')
            });
    }

    const onPageChange = (event, newPage) => setTableData((prev) => ({...prev, page: newPage}))

    const onRowsPerPageChange = (event) => {
        setTableData((prev) => ({...prev, rowsPerPage: event.target.value, page: 0}))
        localStorage.setItem(LOCAL_STORAGE_ITEM_NAME, event.target.value)
    }

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const removeDocumentName = (fileId) => {
        vehicleService.deleteVehicleFile(id, fileId)
            .then(() => {
                retrieveVehicle()
                sendAlertMessage('Fájl sikeresen törölve!')
            })
            .catch(error => sendAlertMessage(error.message, 'error'))
    };

    const updateFileDescription = (id, form) => {
        cloudStorageFileService.update(id, form)
            .then(() => {
                retrieveVehicle()
                sendAlertMessage('Fájl leírása sikeresen módosítva!')
            })
            .catch(error => sendAlertMessage(error.message, 'error'))
    }

    return (
        <VehicleEditorTemplate
            {...{
                id,
                vehicle,
                message,
                onChange,
                create,
                update,
                tableData,
                onPageChange,
                onRowsPerPageChange,
                downloadSimpleInvoice,
                handleFileChange,
                handleRemoveFile,
                removeDocumentName,
                files,
                sendAlertMessage,
                updateFileDescription
            }}
        />
    );
};

export default VehicleEditor;
